// The app's manual — per-screen help text, keyed by route. This ships with the
// app (it documents the app itself, and versions with it); it is deliberately
// NOT Pod config, unlike the institution's identity, which varies per
// deployment. Each entry is app-authored HTML rendered by HelpDrawer via
// {@html}; screens whose "for reference" block needs live Pod data pass that in
// as a `children` snippet at the call site instead.
//
// Voice: `whatFor` addresses the signed-in role in the imperative — what to do
// here; `howItWorks` explains the mechanics the screen touches; `reference`
// carries the longer background (the migrated "for reference" prose and the
// full text of the teaching notes that on-screen were reduced to one-line hints).

export const HELP = {
  login: {
    title: 'Signing in',
    whatFor: `<p>Tell the app what you are here to do. Pick the role that matches
      your work — technical owner, governance owner, or analyst. The app then
      shows only the screens that role acts on.</p>`,
    howItWorks: `<p>You are <em>not</em> signing in as a Solid identity. The
      institution has exactly one WebID, held by the backend as client
      credentials — that is the identity partners grant access to. Your role is
      an org-internal distinction the backend enforces (it rejects, for example,
      a publish attempted without the governance role).</p>`,
    reference: `<p>This Pod talks to exactly one app, and this app serves exactly
      one institution: the relationship is 1:1. Solid's interactive OIDC login
      exists to let <em>many</em> apps act for <em>many</em> people against one
      Pod — real machinery for a real problem, but not this one. So the app holds
      the institution's credential in its backend (the same mechanism the
      connector's system WebID already uses) and tells staff apart by role.</p>
      <p>What this costs is named in <code class="mono">tech-spec.md</code>: the
      backend holds a credential with full Pod access and every partner grant, so
      it must be run by the party the institution trusts — the network, or the
      institution itself, never a vendor. Interactive OIDC per person, with
      bring-your-own identity provider, stays the more decentralised end state.</p>`,
  },

  dashboard: {
    title: 'Dashboard',
    whatFor: `<p>Your starting point. Each card is a piece of work your role owns —
      open the one you need. Cards for other roles aren't shown, so everything
      here is something you can actually act on.</p>`,
    howItWorks: `<p>The figures (record count, periods, partner reachability, cube
      counts) come from one <code class="mono">/api/overview</code> round-trip;
      the connector and theme details are read live from your Pod config. Every
      Pod read happens server-side under the institutional credential — the
      browser never holds a token.</p>`,
  },

  connector: {
    title: 'Source connector',
    whatFor: `<p>Check how the source is wired before you run it: which file it
      reads, where the records land on your Pod, and when it last ran. Then go on
      to Run ingest.</p>
      <p><strong>What each column becomes is not decided here.</strong> The
      analyst decides that in Sources, and the connector simply carries it out —
      so if a record looks wrong, the answer is in Sources → Assess and label,
      not in this screen.</p>`,
    howItWorks: `<p>The connector reads your managed source file,
      applies the mapping the analyst saved, and writes one private record per
      source row to your Pod. This screen renders the configuration read live from
      <code class="mono">connector.json</code> in your Pod — edit it there and
      this screen reflects it with no rebuild.</p>`,
  },

  'connector/run': {
    title: 'Run ingest',
    whatFor: `<p>Pull the source rows newer than the watermark into your Pod, then
      recompute the private cube. Check the pending count, run it, and watch the
      record count climb. When nothing is pending, running is a safe no-op.</p>`,
    howItWorks: `<p>Ingest runs as a background job on the backend from the Pod
      watermark, so a closed tab can't kill it. It writes private ADR records and
      recomputes your <strong>private</strong> cubes — it does
      <strong>not</strong> touch the shared cubes your partners read. Surfacing a
      new period to partners is a separate governance act (Partners → "Update
      shared cubes").</p>`,
  },

  // The Sources flow, in step order. These two carry the vocabulary the rest of
  // the app assumes you already have — the disclosure labels here, the word
  // "cube" next door — so they define their terms rather than use them.
  //
  // "Disclosure" survives as the name of the *concept* (the two labels, the
  // treatment they derive) but no longer names the section: it described the
  // outcome, while every screen inside is about a source. Route ids stay
  // `disclosure/*` — internal and stable, same as `analysis`.
  'disclosure/label': {
    title: 'Assess and label the source',
    whatFor: `<p>Say what each column in your source file actually is. Everything
      else follows from what you decide here — which cubes you can build, and what
      a partner ever gets to see.</p>
      <p>The table reads left to right as a pipeline: what the column holds in
      your file, then the three things you decide about it, then what it
      publishes. Most columns are already answered, so read it as
      <em>check these</em> rather than <em>fill this in</em>. Click any cell to
      change that one thing.</p>
      <p><strong>The shared term is the decision that matters.</strong> It is the
      agreed name for what a column means — "date of birth", "gender identity" —
      the name your partners use for the same thing. Choosing it also settles the
      rest: which record the column belongs to, and how carefully it has to be
      handled. Change those afterwards only where you actually disagree, and the
      app will remember that you did.</p>
      <p><strong>Load a CSV to see real values.</strong> The file is read in this
      browser tab and never uploaded — not to your Pod, not to the app. Flip
      through rows to see the range of what is in the column and what each value
      turns into once your transforms run. You can label without a file; you just
      won't see any values.</p>
      <p>The warnings at the top are the three ways a file and a mapping can
      disagree: columns in the file you have not accounted for, columns your
      mapping expects that this file does not have, and columns still missing an
      answer. All three have to be settled before you can move on.</p>`,
    howItWorks: `<p><strong>Three questions, and the third answers itself.</strong>
      Does this column identify a person — on its own, only in combination with
      others, or not at all? And is what it holds personal, confidential, or not
      sensitive? Those two answers decide the treatment: a column that identifies
      someone on its own is <em>not published at all</em>; one that identifies
      them in combination is <em>published only once coarsened</em>; one that
      identifies nobody but still holds something personal or confidential is
      <em>published on its own, never combined</em>; and only a column that is
      neither is <em>published freely</em>. You can force a different treatment, and that is recorded as
      an override rather than quietly applied.</p>
      <p><strong>Where the prefill comes from.</strong> Each term in the shared
      catalog carries a suggested posture, so picking a term fills both labels in.
      The catalog is a suggestion from the network, not a rule — your override
      travels with the mapping, so a reader can always see that this institution
      decided differently and about what.</p>
      <p><strong>Transforms run between the two.</strong> A transform is what
      turns the value in your file into the value that gets published — matching
      a value onto a shared code, grouping dates into quarters, banding numbers.
      The <em>Published</em> column is the result of actually running the chain on
      the row you are inspecting, so a broken transform shows up as an error in
      that cell rather than at save time.</p>
      <p><strong>Records are a consequence, not a choice.</strong> A record type
      exists because some column's term lands in it, which is why you cannot add
      one directly. Its container is the folder on your Pod the records get
      written to.</p>
      <p>Nothing here has reached your Pod. Edits live in this browser tab until
      you confirm, which is also why closing the tab loses them.</p>`,
    reference: `<p>What you are building is
      <code class="mono">mapping.json</code>, one entry per column: its source
      header, the shared term it publishes as, the transform chain, the two
      labels and the treatment they derive. Columns you choose not to publish are
      listed by name in <code class="mono">drop</code> — recorded as a decision,
      not simply absent.</p>
      <p>"Added by the connector" columns are not from your file at all. They are
      housekeeping the ingest writes itself — provenance back to the raw record,
      the intake timestamp — and they stay in the private record rather than
      reaching a cube.</p>`,
  },

  'disclosure/aggregates': {
    title: 'Build cubes',
    whatFor: `<p>Decide what your institution is willing to count in public.</p>
      <p><strong>A cube is a table of counts.</strong> Pick one thing to break
      down by — say gender identity — and you get a row per value with a number
      beside it: how many records fall into each group. Pick two, and you get
      every combination of the two. That table is the cube. It holds no names, no
      birth dates, no rows about individual people; an aggregate over 300 records is
      the same shape whether you have 300 records or 30,000.</p>
      <p>This is the only kind of thing that ever leaves your Pod. The records
      the cube was counted from stay private, always.</p>
      <p><strong>Small groups are hidden.</strong> Any count under the threshold
      is dropped before the file is written, because a group of one is a person.
      Preview a cube before you add it — a breakdown can be perfectly safe and
      still say almost nothing once the small groups are gone, and it is better
      to find that out here.</p>
      <p>Adding a cube does not share it. It is computed into your private
      container and stays there until the governance owner grants a partner
      access to its theme.</p>`,
    howItWorks: `<p>Each cube is one entry in
      <code class="mono">aggregations.json</code> on your Pod — a name, a theme,
      and the dimensions to group by. Confirm writes that file and recomputes
      every cube from the private records; the counting happens server-side under
      the institutional credential.</p>
      <p>Published cubes are RDF Data Cube
      (<code class="mono">qb:DataSet</code>) documents: one
      <code class="mono">qb:Observation</code> per surviving group, carrying its
      dimension values and the count. "Cube" is the word the file format, the
      manifest, the spec and your partner institutions all use for this, which is
      why the app uses it too.</p>
      <p><strong>Why suppression is not reversible from the file.</strong>
      Suppressed cells are never written, so a cube cannot tell you whether a
      missing combination was hidden or simply never occurred. That is deliberate
      — a placeholder saying "hidden here" would leak the thing suppression
      exists to hide.</p>`,
  },

  records: {
    title: 'Records in Pod',
    whatFor: `<p>Browse the private records held on your Pod and open any one
      as RDF. Filter the loaded page by period. These records never leave the
      Pod — only controlled aggregates counted from them can do so.</p>`,
    howItWorks: `<p>Each record is one Turtle file in a private container chosen
      by its source mapping and readable only by this institution.</p>`,
    reference: `<p>Each record is keyed by the configured source ID strategy and
      carries <code class="mono">prov:wasDerivedFrom</code> /
      <code class="mono">prov:wasGeneratedBy</code> provenance back to the raw
      source row.</p>`,
  },

  partners: {
    title: 'Partners',
    whatFor: `<p>Manage who this institution shares with. Each partner shows the
      themes you grant them, when each cube was last published, and what they
      explicitly communicated as granted to you. Use "Update shared cubes" to
      republish a partner with the current cubes — that is what surfaces a
      newly ingested period to them.</p>`,
    howItWorks: `<p>The list is read from the Pod registry
      (<code class="mono">partners.json</code>) plus live directional probes: the
      outbound container lives on your Pod (present = you publish to them), the
      inbound URLs are exact containers they communicated to you. Each is probed
      directly as your institutional WebID; no reciprocal path is guessed.
      "Nothing communicated" is normal for an outbound-only partner.
      Republishing is a governance act — the ingest job never touches shared
      containers, so a partner only sees a new period once you republish.</p>`,
  },

  partner: {
    title: 'Add sharing partner — details',
    whatFor: `<p>Identify the partner and configure either sharing direction:
      their WebID receives access to cubes you grant, while exact container URLs
      they communicated are recorded as granted by them.</p>`,
    howItWorks: `<p><strong>The WebID is the partner's identity.</strong> Access
      is granted directly to it via a WAC rule on your Pod — there is no account
      on your side. For the reverse direction, give them your institutional
      WebID and record the exact URLs they return. Knowing a URL is discovery;
      WAC still decides whether it is readable.</p>`,
  },

  'partner/access': {
    title: 'Add sharing partner — what you share',
    whatFor: `<p>Select the themes to grant to the partner. You
      share whole themed cubes, not individual records — each theme carries its
      own suppression threshold and sharing policy. Leave all themes unselected
      for an inbound-only partnership.</p>`,
    howItWorks: `<p><strong>Marginals only — never the cross.</strong> Only the
      suppressed single-axis marginal cubes are written to the partner container.
      The private records and the full cross-tabulation stay in your Pod; nothing
      that could re-identify an artist leaves it.</p>`,
  },

  'partner/confirm': {
    title: 'Add sharing partner — review directions',
    whatFor: `<p>Review granted-to and granted-by directions separately.
      Confirming records the partnership, probes communicated inbound URLs, and
      publishes outbound cubes only when themes were selected.</p>`,
    howItWorks: `<p>Inbound URLs are never written to: the backend probes them as
      your institutional WebID and reports readable, grant pending, or
      unavailable. For outbound themes it writes the slices, the
      <code class="mono">manifest.ttl</code> and the WAC
      <code class="mono">.acl</code> as the institutional WebID — the same
      operations as <code class="mono">demo_scripts/setup_05_partner.js</code> — and rejects
      the call outright unless you hold the governance role. A container that
      already exists answers 409 on re-publish; that is "exists", not a
      failure.</p>`,
  },

  catalog: {
    title: 'Catalog — your cubes and the shared ones',
    whatFor: `<p>Start at the top: <strong>Your cubes</strong> is everything this
      Pod computed, whether or not anyone else can see it. Open one to read the
      actual numbers per quarter. Below that, the two shared views — the copy a
      partner reads from you, and the cube they share back.</p>`,
    howItWorks: `<p><strong>Your cubes</strong> reads your private cube
      container directly and reconciles three separate facts about each one: what
      <code class="mono">aggregations.json</code> declares, whether a cube file
      actually exists, and which partner containers hold a copy. Those three
      disagreeing is the point — a cube that was never recomputed, one kept
      deliberately private, and one whose publish never happened are three
      different problems that otherwise all look like an empty panel.</p>
      <p><strong>Private by intent is not a gap.</strong> A cube whose sharing
      mode is <code class="mono">none</code> is meant to stay here. The two states
      that need a human are <em>orphan</em> (a file with no declaration behind it,
      which nothing will ever recompute) and <em>published beyond intent</em> (a
      copy in a partner container while the declaration shares it with
      nobody).</p>
      <p><strong>An absent group is not a zero.</strong> Groups under the
      threshold are dropped before the file is written, so the cube cannot tell
      you whether a missing combination was suppressed or never occurred. And a
      crossed cube summed down to one axis undercounts, silently, for the same
      reason.</p>
      <p>The two sections below read each Pod's
      <code class="mono">manifest.ttl</code> — a fast description of every shared
      cube (theme, dimensions, entity count, measure) written when access was
      granted, enough to profile a cube without reading one observation.</p>
      <p><strong>Different gender semantics stay distinct.</strong> Your Pod
      records act-level composition as
      <code class="mono">adr:genderComposition</code>; the partner uses an
      artist-intake <code class="mono">schema:gender</code> string. The default
      analysis does not merge these unlike axes into one distribution.</p>
      <p><strong>Audience is mutual; operations is not.</strong> Both institutions
      publish audience cubes for reciprocal comparison. The operations cube is
      this institution's unilateral disclosure — nothing operational flows back.
      Distinctive diversity axes also remain separate rather than being treated
      as equivalent (pronouns on one side, trans experience on the other).</p>`,
    reference: `<p>The catalog is driven entirely by each cube's manifest. Each
      dataset carries its theme, dimensions, measure and entity count — enough to
      profile the cube without reading a single observation.</p>`,
  },

  queries: {
    title: 'Queries',
    whatFor: `<p>Three separable jobs, one per facet — not three steps, and you
      can be in the middle of all three at once. Drafts survive switching between
      them.</p>
      <p><strong>Cubes</strong> — build a breakdown your cubes do not carry yet.
      A query can only group by something a cube already counts, so a missing
      breakdown is answered here, not in a cleverer query. What you build is
      computed into your private container and shared with nobody until
      governance republishes it.</p>
      <p><strong>Comparisons</strong> — pick a cube and an axis on each side, name
      the sides, preview, save. The naming is the whole point: it is where you
      assert that two differently-named axes answer the same question, and it
      travels with the saved query as the block heading. The tool cannot work
      that out and does not try. What it does write for you are the caveats that
      follow from the cubes themselves — the crossed-cube undercount, the
      differing vocabularies — into the description that ships with the query.</p>
      <p><strong>Custom queries</strong> — write SPARQL by hand, run it, save it,
      or load an existing named query to fork it. Nothing the other two facets do
      is unavailable here: a compiled comparison is ordinary text, and "Edit as
      custom query" drops it into this editor.</p>`,
    howItWorks: `<p>Named queries live in your Pod's
      <code class="mono">queries.json</code>; the Analysis screen composes one
      panel per entry, so a saved query appears there with no rebuild. Deleting
      one removes the panel.</p>
      <p><strong>You supply the query, the backend supplies the sources.</strong>
      SPARQL runs through Comunica on the app backend over a source list chosen
      there — the shared containers this institution already reads. That is why
      no query, however written, can be aimed at the private record container.</p>
      <p><strong>The result-shape convention.</strong> A result carrying
      <code class="mono">?value</code> and <code class="mono">?count</code> renders
      as distribution blocks; add <code class="mono">?period</code> for quarter
      chips, and <code class="mono">?publisher</code> or
      <code class="mono">?series</code> to split it into one block per side.
      Anything else renders as a plain table — a fallback, not an error.</p>
      <p><strong>Why a cube can be offered but unavailable.</strong> A query reads
      the shared containers — what was published outbound, plus what partners
      share inbound. A cube sitting in your private cube container is
      unreachable however well it reconciles, so the picker lists it, disables it,
      and says which of those it is. The Catalog holds the full picture of every
      cube; the picker deliberately shows only its name and institution.</p>`,
    reference: `<p>Federated SPARQL lets one query span multiple Pod resources
      without copying or caching data centrally. Comunica authenticates as the
      institutional WebID and honours each container's WAC rules; an unreachable
      source contributes an empty result rather than failing the query.</p>`,
  },

  analysis: {
    title: 'Comparisons',
    whatFor: `<p>Read each institution's shared cubes side by side. Each panel is
      one named query held in your Pod, with one block per publisher; re-run to
      pick up a newly published period, or add a query from the editor and it
      appears here.</p>`,
    howItWorks: `<p>The readout is a background job that loads
      <code class="mono">queries.json</code> from your Pod and runs each entry over
      both Pods' shared cube <code class="mono">.ttl</code> files, authenticated as
      the institutional WebID. Nothing here is hardcoded — the panels are composed
      from whatever the Pod holds. The headline gender panel
      <code class="mono">UNION</code>-bridges two vocabularies with no shared
      schema.</p>
      <p><strong>Blocks, not a shared row axis.</strong> Each publisher keeps its
      own vocabulary, its own denominator and its own percentages, so nothing here
      depends on deciding that <code class="mono">Female</code> and
      <code class="mono">Femenino</code> are the same value. The columns are the
      comparison; aligning the labels is a separate question this screen
      deliberately does not answer.</p>
      <p><strong>An empty block is a statement.</strong> A publisher that shares no
      cube answering a query still gets a labelled block, because "publishes
      nothing on this axis" and "is not part of this comparison" are different
      facts. Publishers you cannot read at all get no block — you know nothing
      about what they publish.</p>`,
  },
};
